import { Plugs, ShareNetwork, ShoppingBag } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import Modal from './Modal/Modal';
import NumberedSteps, { type NumberedStep } from './NumberedSteps';

/**
 * Qué es el heru-cash, contado en una pantalla.
 *
 * Nace del heatmap de la pantalla de vinculación: el ícono de monedas de
 * `LinkingRewardBanner` concentra clics aunque no fuera clickeable — el usuario
 * ya estaba preguntando qué es eso que le prometemos, y la franja no tenía
 * respuesta que dar.
 *
 * ES LA MISMA SUPERFICIE QUE EL MODAL DE REFERIDOS, NO UNA PARECIDA: comparte
 * su lista de pasos (`NumberedSteps`), su jerarquía —título con `heru-cash` en
 * azul, subtítulo gris, pastilla verde para el número que importa— y su voz en
 * segunda persona. Son dos puertas al mismo producto; que se vean iguales es el
 * punto, y por eso el estilo se hereda de un componente compartido en vez de
 * copiarse.
 *
 * PURO Y EN molecules: las pantallas de onboarding v3 sólo pueden importar de
 * esa allowlist (purity.test.ts). Recibe el monto ya resuelto y no decide nada.
 *
 * EL COPY NO INVENTA REGLAS: la paridad `1 heru-cash = $1 MXN` y el 25% del
 * referido viven en `ReferralsModal`; que también paga las renovaciones
 * automáticas vive en `ReferralsWelcomeModal` y `PlanChangeCashModal`. Es una
 * sola moneda contada en varias superficies; si una regla cambia, cambia en todas.
 */
const explainerSteps = (rewardAmount: number | string): NumberedStep[] => [
	{
		icon: Plugs,
		title: 'Vincula tu cuenta del SAT',
		desc: 'Conéctala con tu e.firma o con RFC y contraseña.',
		reward: `Gana $${rewardAmount} de heru-cash al vincular`,
	},
	{
		icon: ShoppingBag,
		title: 'Úsalo en renovaciones y compras',
		desc: 'Se descuenta del total de tu plan, declaración o trámite.',
	},
	{
		icon: ShareNetwork,
		title: 'Invita y gana más',
		desc: 'Por cada amigo/a que haga su primera compra, ganas el 25%.',
	},
];

const HeruCashExplainerModal = ({
	open,
	onClose,
	rewardAmount,
}: {
	open: boolean;
	onClose: () => void;
	/** number desde el hook legacy, string desde el ctx del motor de onboarding.
	 * Sólo se interpola, igual que en el banner. */
	rewardAmount: number | string;
}) => (
	<Modal open={open} onClose={onClose} sx='max-w-md'>
		<div className='flex w-full flex-col py-2'>
			<h3 className='text-xl font-extrabold tracking-tight text-balance text-h-text-heading'>
				Gana y usa <span className='text-h-primary-30'>heru-cash</span>
			</h3>
			<p className='mt-1 mb-5 text-[13px] leading-relaxed text-h-text-medium'>
				Es saldo a tu favor dentro de heru: 1 heru-cash equivale a $1 MXN de
				descuento en tus renovaciones y compras.
			</p>

			<NumberedSteps steps={explainerSteps(rewardAmount)} />

			<div className='my-1 border-t border-h-neutral-90' />

			<p className='mt-3 text-xs text-h-text-medium'>
				heru-cash se aplica en automático en tus próximas renovaciones, y
				también puedes usarlo en tus compras.
			</p>

			<Button
				haptic
				onClick={onClose}
				className='mt-4 h-12 w-full rounded-xl bg-h-primary-30 font-semibold text-white'
			>
				Entendido
			</Button>
		</div>
	</Modal>
);

export default HeruCashExplainerModal;
